import { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType";
import QHPNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPNetworkFiltersTable";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { MedicareNetwork, MedicareParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { QHPParentOrganization } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganization";
import MedicareNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareNetworkFiltersTable";
import { MedicaidParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganization";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import MedicaidNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidNetworkFiltersTable";
import { Accordion } from "@app/shared/Accordion";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";

interface CompetitiveInsightsMapNetworksProps {
  networks: BenchmarkingNetworkType[];
  networkSearchValue: string;
  parentOrganization: BenchmarkingParentOrganizationType;
}

const CompetitiveInsightsMapNetworks = (props: CompetitiveInsightsMapNetworksProps): JSX.Element => {
  const [defaultOpen, setDefaultOpen] = useState<boolean>(false);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  useEffect(() => {
    // on load, if there are selected contracts (from a previous search, or from applying service area),
    // open the accordion
    if (cloneDeep(props.networks).map(x => x.contractId).some(
      y => benchmarkingFiltersContext.selectedNetworks.map(n => n.contractId).includes(y)
    ) || !!props.networkSearchValue) {
      if (!defaultOpen) {
        setDefaultOpen(true);
      }
    } else {
      if (defaultOpen) {
        setDefaultOpen(false);
      }
    }
  }, [props.networks, props.networkSearchValue]);

  const getNetworksByLineOfBusiness = (parentOrganization: BenchmarkingParentOrganizationType) => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (parentOrganization as MedicareParentOrganization).hContracts;
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (parentOrganization as QHPParentOrganization).hContracts;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return [(parentOrganization as MedicaidParentOrganization).hContract];
      default:
        return;
    }
  }
  const getAccordionTitle = (parentOrganization: BenchmarkingParentOrganizationType) => {
    return (
      <div>
        <span className="text-info">
          <EllipsisTooltip
            text={parentOrganization.parentOrganizationName}
          />
        </span>
        {benchmarkingFiltersContext.selectedLineOfBusiness !== BenchmarkingLineOfBusinessEnum.Medicaid && (
          <span className="competitive-insights__map--accordion-count fa-stack ms-2">
            <i className="fa fa-circle fa-stack-2x map-network-count-circle" />
            <b className="fa-stack-1x map-network-count-number">{getNetworksByLineOfBusiness(parentOrganization).length}</b>
          </span>
        )}
      </div>
    )
  }

  const getNetworkFiltersTable = (): JSX.Element => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (
          <MedicareNetworkFiltersTable
            data={props.networks as MedicareNetwork[]}
            isMapView={true}
            parentOrganizationName={props.parentOrganization.parentOrganizationName}
            selectedContractIds={benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId)}
          />
        );
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (
          <QHPNetworkFiltersTable
            data={props.networks as QHPNetwork[]}
            isMapView={true}
            parentOrganizationName={props.parentOrganization.parentOrganizationName}
            selectedContractIds={benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId)}
          />
        );
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return (
          <MedicaidNetworkFiltersTable
            data={props.networks as MedicaidNetwork[]}
            isMapView={true}
            parentOrganizationName={props.parentOrganization.parentOrganizationName}
            selectedContractIds={benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId)}
          />
        );
      default:
        return;
    }
  }

  const hasSelectedNetworks = getNetworksByLineOfBusiness(props.parentOrganization).map(x => x.contractId).some(x => benchmarkingFiltersContext.selectedNetworks.map(y => y.contractId).includes(x));

  return (
    <Accordion
      className={`competitive-insights__map--accordion${hasSelectedNetworks ? " selected-parent-organization" : ""}`}
      title={getAccordionTitle(props.parentOrganization)}
      defaultOpen={defaultOpen || benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid}
      testId={`competitive-insights__map--accordion-${props.parentOrganization.parentOrganizationName}`}
    >
      <div>
        {getNetworkFiltersTable()}
      </div>
    </Accordion>
  );
}

export default CompetitiveInsightsMapNetworks;
